/**
 * Platforms-panel sort logic (REQ-UI-110).
 *
 * The pure sort for the Platforms list — the default order + the
 * selection-driven sort that joins the live status on ``producer_id`` for the
 * status-derived fields. Lives in this non-component module (not in
 * :file:`./platforms-panel.tsx`) so the panel file exports only its component
 * (fast-refresh purity) and the sort is unit-testable in isolation — the same
 * split as :file:`@/alerts/alert-display` / :file:`@/detections/detection-display`.
 *
 * Cross-references:
 *   - REQ-UI-110 (operator list sort) / REQ-UI-046 (health severity order) /
 *     REQ-UI-051 (sort coexists with id-keyed selection — non-mutating).
 *   - storyboard Flow 05 Frame 1 (health severity order: offline → degraded →
 *     unknown → healthy).
 *   - @/lib/list-sort (the engine + comparator kit).
 */
import type { ProducerListItem } from "@/platforms/use-producers";
import type { ProducerStatusItem } from "@/platforms/use-producers-status";
import { healthStateToMarkerStatus } from "@/map/marker-styling";
import {
  type ComparatorMap,
  compareByRank,
  compareIso,
  compareNumbers,
  compareStringsCI,
  healthSeverityRank,
  type SortSelection,
  sortBy,
} from "@/lib/list-sort";
import type { PlatformListSort } from "@/auth/session-types";

/**
 * The Platforms panel's built-in default order: display name ascending
 * (REQ-UI-110; Flow 05 Frame 1). Used when the working state carries no
 * ``list_sort.platforms`` selection.
 */
export const PLATFORM_DEFAULT_SORT: SortSelection<PlatformListSort["field"]> = {
  field: "display_name",
  direction: "asc",
};

/**
 * Sort the metadata ``items`` by the operator's selection (REQ-UI-110),
 * joining the live status on ``producer_id`` for the status-derived fields.
 *
 * The comparators close over ``statusByProducer`` so ``health`` /
 * ``last_status_time`` / ``detection_count`` read the merged live status — the
 * same join the row render uses, so the sort key always agrees with what the
 * row shows. ``health`` sorts on the **derived** :type:`MarkerStatus` category
 * (via the panel's existing :func:`healthStateToMarkerStatus`, reused verbatim)
 * by operator severity rank, never the raw enum int. ``last_status_time`` is
 * nullable — a registered-but-silent producer (no status, or a null
 * last-status) routes to the tail via the ``isAbsent`` predicate so the silent
 * producers sink regardless of direction (REQ-UI-110 nulls-last).
 * ``detection_count`` defaults to 0 for a statusless producer (a real value,
 * not an absence — 0 detections is meaningful), so it is NOT routed to the tail.
 *
 * @param items - The producer metadata list (the universe of rows).
 * @param statusByProducer - Live status indexed by ``producer_id``.
 * @param selection - The active sort selection (panel default when none).
 * @returns A new, sorted ``items`` array (the source is not mutated).
 */
export function sortPlatformRows(
  items: readonly ProducerListItem[],
  statusByProducer: ReadonlyMap<string, ProducerStatusItem>,
  selection: SortSelection<PlatformListSort["field"]>,
): ProducerListItem[] {
  const comparators: ComparatorMap<ProducerListItem, PlatformListSort["field"]> = {
    display_name: (a, b) => compareStringsCI(a.display_name, b.display_name),
    health: compareByRank((p) =>
      healthSeverityRank(
        healthStateToMarkerStatus(statusByProducer.get(p.producer_id)?.health_state ?? 0),
      ),
    ),
    last_status_time: (a, b) =>
      // Both present here (absent ones are partitioned out by isAbsent below).
      compareIso(
        statusByProducer.get(a.producer_id)?.last_status_time ?? "",
        statusByProducer.get(b.producer_id)?.last_status_time ?? "",
      ),
    detection_count: (a, b) =>
      compareNumbers(
        statusByProducer.get(a.producer_id)?.detection_count ?? 0,
        statusByProducer.get(b.producer_id)?.detection_count ?? 0,
      ),
  };
  // last_status_time is the only nullable platform sort field: a producer with
  // no status row, or a null last-status, has no time to sort on → tail.
  const isAbsent =
    selection.field === "last_status_time"
      ? (p: ProducerListItem): boolean =>
          (statusByProducer.get(p.producer_id)?.last_status_time ?? null) === null
      : undefined;
  return sortBy(items, selection, comparators, isAbsent);
}
